import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, interval, of, throwError } from 'rxjs';
import { switchMap, catchError, takeUntil, filter, timeout } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { RestfulEndpoint } from '../restful-endpoint.service';
import { LoadingExclusionService } from '../../shared/services/loading-exclusion.service';
import { parseServerDate } from '../../shared/utils/server-date.util';
import {
  QueueStatus,
  QueueStatusResponse,
  ProgressBarState,
  SyncResultsModalState,
  SyncResults,
  SyncProvider,
  TypedSyncResult,
  ProjectsSyncResult,
  P6SyncResult,
  ObservationsSyncResult,
  QueueTracker,
  QueueTrackerData,
} from '../../models/integration.model';
import { environment } from '../../../environments/environment';

/**
 * Queue Status Service for tracking Unifier sync progress
 * Handles polling of active queue statuses and progress updates
 * Following existing service patterns in the codebase
 */
@Injectable({
  providedIn: 'root',
})
export class QueueStatusService implements OnDestroy {
  private readonly API_BASE = 'integration/projects/queue-statuses';
  private readonly POLLING_INTERVAL = environment.queue_status_polling_interval;
  private readonly TRACKING_INTERVAL = 500; // 500 milliseconds
  private readonly TRACKING_INTERVAL_PROJECTS = 2500; // 2.5 second
  private readonly TRACKING_TIMEOUT = 30 * 60 * 1000; // 30 minute
  private readonly SYNC_RESULTS_MODAL_TIMEOUT = 3 * 60 * 1000; // 3 min

  // Provider-specific localStorage keys
  private readonly QUEUE_KEYS: { [key in SyncProvider]: string } = {
    projects: 'last_processed_projects_sync_queue',
    p6: 'last_processed_p6_sync_queue',
    observations: 'last_processed_observations_sync_queue',
  };

  // Provider-specific last queue status ID keys
  private readonly LAST_QUEUE_STATUS_KEYS: { [key in SyncProvider]: string } = {
    projects: 'last_queue_status_id_projects',
    p6: 'last_queue_status_id_p6',
    observations: 'last_queue_status_id_observations',
  };

  /**
   * Get project-specific localStorage key
   */
  private getProjectSpecificKey(provider: SyncProvider, projectId?: string): string {
    const baseKey = this.QUEUE_KEYS[provider];

    // Projects sync is global, P6 and observations are project-specific
    if ((provider === 'p6' || provider === 'observations') && projectId) {
      return `${baseKey}_${projectId}`;
    }

    return baseKey;
  }

  /**
   * Get project-specific localStorage key for last queue status ID
   */
  private getLastQueueStatusKey(provider: SyncProvider, projectId?: string): string {
    const baseKey = this.LAST_QUEUE_STATUS_KEYS[provider];

    // Projects sync is global, P6 and observations are project-specific
    if ((provider === 'p6' || provider === 'observations') && projectId) {
      return `${baseKey}_${projectId}`;
    }

    return baseKey;
  }

  // Provider-specific queue trackers
  private queueTrackers: QueueTracker = {
    projects: this.loadQueueTracker('projects'),
    p6: this.loadQueueTracker('p6'),
    observations: this.loadQueueTracker('observations'),
  };

  // Backward compatibility - keep the old property for projects
  public readonly LAST_PROCESSED_SYNC_QUEUE_KEY = this.QUEUE_KEYS.projects;
  public last_processed_sync_queue = this.queueTrackers.projects;

  // Provider-specific progress state observables
  // For projects: global progress state
  // For p6 and observations: project-specific progress states
  private _progressStates$: { [key in SyncProvider]: BehaviorSubject<ProgressBarState> } = {
    projects: new BehaviorSubject<ProgressBarState>({
      isVisible: false,
      percentage: 0,
      currentCount: 0,
      totalCount: 0,
      message: '',
      arabicMessage: '',
      status: 'processing',
    }),
    p6: new BehaviorSubject<ProgressBarState>({
      isVisible: false,
      percentage: 0,
      currentCount: 0,
      totalCount: 0,
      message: '',
      arabicMessage: '',
      status: 'processing',
    }),
    observations: new BehaviorSubject<ProgressBarState>({
      isVisible: false,
      percentage: 0,
      currentCount: 0,
      totalCount: 0,
      message: '',
      arabicMessage: '',
      status: 'processing',
    }),
  };

  // Project-specific progress states for P6 and observations
  private _projectProgressStates$: Map<string, BehaviorSubject<ProgressBarState>> = new Map();

  private _syncResults$ = new BehaviorSubject<SyncResultsModalState>({
    isVisible: false,
    results: null,
    testMode: false,
  });

  // Private properties
  private _isPolling = false;
  private _currentQueueStatusId: number | null = null;
  private _pollingSubscription: Subscription | null = null;
  private _trackingSubscription: Subscription | null = null; // Add separate subscription for tracking
  private _unsubscribeAll = new Subject<void>();

  // Timeout tracking for cleanup
  private _pollingTimeoutId: number | null = null;
  private _trackingTimeoutId: number | null = null;
  private _mainTrackingTimeoutId: number | null = null;

  // Emits when a queue completes so components can react (e.g., update UI badges)
  private _queueCompleted$ = new Subject<{
    provider: SyncProvider;
    projectId?: string;
    queueStatusId: number;
  }>();

  constructor(
    private endPoint: RestfulEndpoint,
    private loadingExclusionService: LoadingExclusionService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public properties
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get provider-specific progress state observable
   * For projects: returns global progress state
   * For p6 and observations: returns project-specific progress state
   */
  getProgressState$(
    provider: SyncProvider,
    projectId?: string,
    asSubject: boolean = false,
  ): Observable<ProgressBarState> | BehaviorSubject<ProgressBarState> {
    // Projects sync is global, no project ID needed
    if (provider === 'projects') {
      return asSubject
        ? this._progressStates$[provider]
        : this._progressStates$[provider].asObservable();
    }

    // For P6 and observations, use project-specific progress state
    if (provider === 'p6' || provider === 'observations') {
      if (!projectId) {
        console.warn(`Project ID is required for ${provider} progress state`);
        return asSubject
          ? this._progressStates$[provider]
          : this._progressStates$[provider].asObservable();
      }

      const key = `${provider}_${projectId}`;

      // Create project-specific progress state if it doesn't exist
      if (!this._projectProgressStates$.has(key)) {
        this._projectProgressStates$.set(
          key,
          new BehaviorSubject<ProgressBarState>({
            isVisible: false,
            percentage: 0,
            currentCount: 0,
            totalCount: 0,
            message: '',
            arabicMessage: '',
            status: 'processing',
          }),
        );
      }

      return asSubject
        ? this._projectProgressStates$.get(key)!
        : this._projectProgressStates$.get(key)!.asObservable();
    }

    // Fallback to provider-specific state
    return asSubject
      ? this._progressStates$[provider as keyof typeof this._progressStates$]
      : this._progressStates$[provider as keyof typeof this._progressStates$].asObservable();
  }

  get syncResults$(): Observable<SyncResultsModalState> {
    return this._syncResults$.asObservable();
  }

  get isPolling(): boolean {
    return this._isPolling;
  }

  /**
   * Stream that emits when a queue completes
   */
  get queueCompleted$(): Observable<{
    provider: SyncProvider;
    projectId?: string;
    queueStatusId: number;
  }> {
    return this._queueCompleted$.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get active queue statuses for a specific provider
   * GET /api/queue-statuses/active
   *
   * Note: This request is automatically excluded from the loading bar via URL pattern matching.
   * If you need to exclude other requests, you can use:
   * this.loadingExclusionService.getExclusionHeaders()
   */
  getActiveQueueStatuses(
    provider: SyncProvider = 'projects',
    projectId?: string,
  ): Observable<QueueStatus[]> {
    let url = this.API_BASE + `/active?provider=${provider}`;
    if (projectId && (provider === 'p6' || provider === 'observations')) {
      url += `&project=${projectId}`;
    }
    return this.endPoint
      .get(url, null, null, {
        version: '',
      })
      .pipe(
        switchMap((response: QueueStatusResponse) => {
          if (response.status === 'success' && response.data) {
            const fourHourAgo = Date.now() - 4 * 60 * 60 * 1000;
            const queues = Array.isArray(response.data) ? response.data : [response.data];

            return of(
              queues.filter((item: QueueStatus) => {
                if (
                  parseServerDate(item.started_at).getTime() < fourHourAgo &&
                  item.status === 'processing'
                ) {
                  return false;
                }
                return true;
              }),
            );
          } else {
            return throwError(
              () => new Error(response.message || 'Failed to retrieve active queue statuses'),
            );
          }
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  /**
   * Get specific queue status details
   * GET /api/queue-statuses/{queueStatusId}
   */
  getQueueStatusDetails(queueStatusId: number): Observable<QueueStatus> {
    return this.endPoint.get(`${this.API_BASE}/${queueStatusId}`, null, null, { version: '' }).pipe(
      switchMap((response: QueueStatusResponse) => {
        if (response.status === 'success' && response.data) {
          return of(Array.isArray(response.data) ? response.data[0] : response.data);
        } else {
          return throwError(
            () => new Error(response.message || 'Failed to retrieve queue status details'),
          );
        }
      }),
      catchError(this.handleError.bind(this)),
    );
  }

  /**
   * Get completed queue statuses for a specific provider
   * GET /api/queue-statuses/completed
   */
  getCompletedQueueStatuses(
    provider: SyncProvider = 'projects',
    projectId?: number,
    limit: number = 10,
  ): Observable<QueueStatus[]> {
    const params: any = { provider, limit };
    if (projectId) {
      params.project = projectId;
    }

    return this.endPoint.get(this.API_BASE + '/completed', { params }, null, { version: '' }).pipe(
      switchMap((response: QueueStatusResponse) => {
        if (response.status === 'success' && response.data) {
          return of(Array.isArray(response.data) ? response.data : [response.data]);
        } else {
          return throwError(
            () => new Error(response.message || 'Failed to retrieve completed queue statuses'),
          );
        }
      }),
      catchError(this.handleError.bind(this)),
    );
  }

  /**
   * Get last completed queue status for a specific provider
   * GET /api/queue-statuses/last-completed
   */
  getLastCompletedQueueStatus(
    provider: SyncProvider = 'projects',
    projectId?: string,
  ): Observable<QueueStatus | null> {
    let url = this.API_BASE + `/last-completed?provider=${provider}`;
    if (projectId && (provider === 'p6' || provider === 'observations')) {
      url += `&project=${projectId}`;
    }
    return this.endPoint.get(url, null, null, { version: '' }).pipe(
      switchMap((response: QueueStatusResponse) => {
        if (response.status === 'success' && response.data) {
          const dataArray = Array.isArray(response.data) ? response.data : [response.data];
          if (dataArray.length > 0) {
            return of(dataArray[0]);
          }
        }
        return of(null);
      }),
      catchError((error) => {
        console.error('Error fetching last completed queue status:', error);
        return of(null);
      }),
    );
  }

  /**
   * Start polling for active queue statuses
   */
  startPolling(provider: SyncProvider = 'projects', projectId?: string): void {
    if (this._isPolling) {
      return;
    }

    this._isPolling = true;
    this.pollSequentially(provider, projectId);
  }

  /**
   * Poll sequentially - wait for each request to complete before starting the next one
   */
  private pollSequentially(provider: SyncProvider, projectId?: string): void {
    if (!this._isPolling) {
      return;
    }

    this.getActiveQueueStatuses(provider, projectId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (queueStatuses: QueueStatus[]) => {
          if (queueStatuses.length > 0) {
            this.handleQueueStatusUpdate(queueStatuses, provider, projectId);
          }

          // Schedule next poll after current request completes
          if (this._isPolling) {
            this._pollingTimeoutId = window.setTimeout(() => {
              this._pollingTimeoutId = null;
              this.pollSequentially(provider, projectId);
            }, this.POLLING_INTERVAL);
          }
        },
        error: (error) => {
          console.error('Queue status polling error:', error);
          // Continue polling even if individual requests fail
          if (this._isPolling) {
            this._pollingTimeoutId = window.setTimeout(() => {
              this._pollingTimeoutId = null;
              this.pollSequentially(provider, projectId);
            }, this.POLLING_INTERVAL);
          }
        },
      });
  }

  /**
   * Start polling immediately
   */
  startPollingImmediately(provider: SyncProvider = 'projects', projectId?: string): void {
    this.getActiveQueueStatuses(provider, projectId).subscribe({
      next: (queueStatuses: QueueStatus[]) => {
        this.handleQueueStatusUpdate(queueStatuses, provider, projectId);
      },
      error: (error) => {
        console.error('Queue status polling error:', error);
        // Continue polling even if individual requests fail
      },
    });
  }

  /**
   * Stop polling
   */
  stopPolling(): void {
    this._isPolling = false;

    // Clear pending polling timeout
    if (this._pollingTimeoutId !== null) {
      clearTimeout(this._pollingTimeoutId);
      this._pollingTimeoutId = null;
    }

    if (this._pollingSubscription) {
      this._pollingSubscription.unsubscribe();
      this._pollingSubscription = null;
    }
  }

  /**
   * Start tracking progress for a specific queue
   */
  startTracking(queueStatusId: number, projectId?: string): void {
    this._currentQueueStatusId = queueStatusId;

    // Initial update
    this.updateProgressFromQueueStatusId(queueStatusId, projectId);

    // Start sequential tracking
    this.trackSequentially(queueStatusId, projectId);

    // Set a timeout to stop tracking after TRACKING_TIMEOUT milliseconds
    this._mainTrackingTimeoutId = window.setTimeout(() => {
      if (this._currentQueueStatusId === queueStatusId) {
        console.warn(
          'Tracking timeout reached, stopping tracking for queue status:',
          queueStatusId,
        );
        this.stopTracking();
        this._mainTrackingTimeoutId = null;
      }
    }, this.TRACKING_TIMEOUT);
  }

  /**
   * Track progress sequentially - wait for each request to complete before starting the next one
   */
  private trackSequentially(queueStatusId: number, projectId?: string): void {
    if (this._currentQueueStatusId !== queueStatusId) {
      return; // Stop tracking if we're no longer tracking this queue
    }

    const isProjectsPoll = !projectId;

    this.getQueueStatusDetails(queueStatusId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (queueStatus: QueueStatus) => {
          if (this._currentQueueStatusId === queueStatusId) {
            this.updateProgress(queueStatus, projectId);

            // Schedule next tracking request after current request completes
            if (this._currentQueueStatusId === queueStatusId) {
              this._trackingTimeoutId = window.setTimeout(
                () => {
                  this._trackingTimeoutId = null;
                  this.trackSequentially(queueStatusId, projectId);
                },
                isProjectsPoll ? this.TRACKING_INTERVAL_PROJECTS : this.TRACKING_INTERVAL,
              );
            }
          }
        },
        error: (error) => {
          console.error('Error monitoring job progress:', error);
          // Continue tracking even if individual requests fail
          if (this._currentQueueStatusId === queueStatusId) {
            this._trackingTimeoutId = window.setTimeout(
              () => {
                this._trackingTimeoutId = null;
                this.trackSequentially(queueStatusId, projectId);
              },
              isProjectsPoll ? this.TRACKING_INTERVAL_PROJECTS : this.TRACKING_INTERVAL,
            );
          }
        },
      });
  }

  /**
   * Stop tracking current progress
   */
  stopTracking(timeout?: number, provider: SyncProvider = 'projects', projectId?: string): void {
    //this is the current job being tracked, very important to reset it
    this._currentQueueStatusId = null;

    // Clear pending tracking timeout
    if (this._trackingTimeoutId !== null) {
      clearTimeout(this._trackingTimeoutId);
      this._trackingTimeoutId = null;
    }

    // Clear main tracking timeout
    if (this._mainTrackingTimeoutId !== null) {
      clearTimeout(this._mainTrackingTimeoutId);
      this._mainTrackingTimeoutId = null;
    }

    // Manually unsubscribe from tracking subscription (if it exists)
    if (this._trackingSubscription) {
      this._trackingSubscription.unsubscribe();
      this._trackingSubscription = null;
    }

    if (timeout) {
      const timeoutId = setTimeout(() => {
        if (provider === 'projects') {
          // Projects sync is global
          this._progressStates$[provider].next({
            isVisible: false,
            percentage: 0,
            currentCount: 0,
            totalCount: 0,
            message: '',
            arabicMessage: '',
            status: 'processing',
          });
        } else if (provider === 'p6' || provider === 'observations') {
          // P6 and observations are project-specific
          if (projectId) {
            const key = `${provider}_${projectId}`;
            if (this._projectProgressStates$.has(key)) {
              this._projectProgressStates$.get(key)!.next({
                isVisible: false,
                percentage: 0,
                currentCount: 0,
                totalCount: 0,
                message: '',
                arabicMessage: '',
                status: 'processing',
              });
            }
          } else {
            console.warn(`Project ID is required for ${provider} timeout handling`);
            // Fallback to provider-specific state
            this._progressStates$[provider].next({
              isVisible: false,
              percentage: 0,
              currentCount: 0,
              totalCount: 0,
              message: '',
              arabicMessage: '',
              status: 'processing',
            });
          }
        }
        clearTimeout(timeoutId);
      }, timeout);
    }
  }

  /**
   * Show sync results modal
   */
  showResultsModal(results: SyncResults, testMode: boolean = false): void {
    this._syncResults$.next({
      isVisible: true,
      results,
      testMode,
    });
  }

  /**
   * Hide sync results modal
   */
  hideResultsModal(): void {
    this._syncResults$.next({
      isVisible: false,
      results: null,
      testMode: false,
    });
  }

  /**
   * Hide the progress bar for a specific provider and project
   */
  hideProgressBar(provider: SyncProvider = 'projects', projectId?: string): void {
    if (provider === 'projects') {
      // Projects sync is global
      this._progressStates$[provider].next({
        isVisible: false,
        percentage: 0,
        currentCount: 0,
        totalCount: 0,
        message: '',
        arabicMessage: '',
        status: 'processing',
      });
    } else if (provider === 'p6' || provider === 'observations') {
      // P6 and observations are project-specific
      if (projectId) {
        const key = `${provider}_${projectId}`;
        if (this._projectProgressStates$.has(key)) {
          this._projectProgressStates$.get(key)!.next({
            isVisible: false,
            percentage: 0,
            currentCount: 0,
            totalCount: 0,
            message: '',
            arabicMessage: '',
            status: 'processing',
          });
        }
      } else {
        console.warn(`Project ID is required for hiding ${provider} progress bar`);
        // Fallback to provider-specific state
        this._progressStates$[provider].next({
          isVisible: false,
          percentage: 0,
          currentCount: 0,
          totalCount: 0,
          message: '',
          arabicMessage: '',
          status: 'processing',
        });
      }
    }
  }

  /**
   * Get queue tracker for a specific provider (and project if applicable)
   */
  getQueueTracker(provider: SyncProvider, projectId?: string) {
    // For project-specific providers, load from project-specific storage
    if ((provider === 'p6' || provider === 'observations') && projectId) {
      return this.loadQueueTracker(provider, projectId);
    }

    // For global providers, use in-memory tracker
    return this.queueTrackers[provider];
  }

  /**
   * Update queue tracker for a specific provider (and project if applicable)
   */
  updateQueueTracker(provider: SyncProvider, data: Partial<QueueTrackerData>, projectId?: string) {
    if ((provider === 'p6' || provider === 'observations') && projectId) {
      // For project-specific providers, load current tracker, update it, and save it
      const currentTracker = this.loadQueueTracker(provider, projectId);
      const updatedTracker = { ...currentTracker, ...data };
      this.saveQueueTracker(provider, projectId, updatedTracker);
    } else {
      // For global providers, update in-memory tracker
      this.queueTrackers[provider] = { ...this.queueTrackers[provider], ...data };
      this.saveQueueTracker(provider);
    }
  }

  /**
   * Get last sync result for a specific provider with type safety
   */
  getLastSyncResult(provider: SyncProvider, projectId?: string): TypedSyncResult | null {
    const tracker = this.getQueueTracker(provider, projectId);
    if (!tracker?.results) return null;

    // Transform the raw results into typed results
    return this.transformToTypedResult(provider, tracker);
  }

  /**
   * Save last queue status ID to localStorage
   */
  saveLastQueueStatusId(provider: SyncProvider, queueStatusId: number, projectId?: string): void {
    const key = this.getLastQueueStatusKey(provider, projectId);
    localStorage.setItem(key, queueStatusId.toString());
  }

  /**
   * Get last queue status ID from localStorage
   */
  getLastQueueStatusId(provider: SyncProvider, projectId?: string): number | null {
    const key = this.getLastQueueStatusKey(provider, projectId);
    const savedId = localStorage.getItem(key);
    return savedId ? parseInt(savedId, 10) : null;
  }

  /**
   * Remove last queue status ID from localStorage
   */
  removeLastQueueStatusId(provider: SyncProvider, projectId?: string): void {
    const key = this.getLastQueueStatusKey(provider, projectId);
    localStorage.removeItem(key);
  }

  /**
   * Check for saved processing queue on component remount
   * If there are no active queues, check if the saved queue is still processing
   * If yes, track it; if not, get its results and clean up
   */
  checkSavedProcessingQueue(provider: SyncProvider, projectId?: string): void {
    const savedQueueId = this.getLastQueueStatusId(provider, projectId);
    if (!savedQueueId) {
      return;
    }

    // First check if there are active queues
    this.getActiveQueueStatuses(provider, projectId).subscribe({
      next: (activeQueues: QueueStatus[]) => {
        const hasActiveQueues = activeQueues.length > 0;

        if (hasActiveQueues) {
          return;
        }

        // No active queues, check the saved queue status
        this.getQueueStatusDetails(savedQueueId).subscribe({
          next: (queueStatus: QueueStatus) => {
            if (queueStatus.status === 'processing') {
              this.startTracking(savedQueueId, projectId);
            } else {
              // Handle completion like we do in normal flow
              this.updateProgress(queueStatus, projectId);

              // Remove the saved queue status ID since it's now completed
              this.removeLastQueueStatusId(provider, projectId);
            }
          },
          error: (error) => {
            console.error(`Error checking saved queue ${savedQueueId}:`, error);
            // If we can't fetch the queue details, remove the saved ID to prevent future errors
            this.removeLastQueueStatusId(provider, projectId);
          },
        });
      },
      error: (error) => {
        console.error(`Error checking active queues for ${provider}:`, error);
        // Still try to check the saved queue even if active queue check fails
        this.getQueueStatusDetails(savedQueueId).subscribe({
          next: (queueStatus: QueueStatus) => {
            if (queueStatus.status === 'processing') {
              this.startTracking(savedQueueId, projectId);
            } else {
              this.updateProgress(queueStatus, projectId);

              this.removeLastQueueStatusId(provider, projectId);
            }
          },
          error: (error) => {
            console.error(`Error checking saved queue ${savedQueueId}:`, error);
            this.removeLastQueueStatusId(provider, projectId);
          },
        });
      },
    });
  }

  /**
   * Check for last completed queue from backend on component mount
   * If found and newer than localStorage data, save it and update UI
   */
  checkLastCompletedQueue(provider: SyncProvider, projectId?: string): void {
    this.getLastCompletedQueueStatus(provider, projectId).subscribe({
      next: (queueStatus: QueueStatus | null) => {
        if (!queueStatus) {
          return;
        }

        // Get current tracker from localStorage
        const currentTracker = this.getQueueTracker(provider, projectId);

        // Compare timestamps - only proceed if backend data is newer
        const backendTimestamp = queueStatus.completed_at
          ? parseServerDate(queueStatus.completed_at).getTime()
          : 0;
        const localTimestamp = currentTracker?.timestamp
          ? new Date(currentTracker.timestamp).getTime()
          : 0;

        if (backendTimestamp > localTimestamp) {
          // Backend has newer data, update localStorage
          const projectIdForQueue = queueStatus.project_id?.toString();

          this.updateQueueTracker(
            provider,
            {
              id: queueStatus.id,
              completed: true,
              status: queueStatus.status,
              viewed: false,
              results: queueStatus.latest_progress?.results,
              timestamp: queueStatus.completed_at
                ? parseServerDate(queueStatus.completed_at)
                : undefined,
              provider: provider,
            },
            projectIdForQueue,
          );

          // Emit queue completed event to update UI badges
          this._queueCompleted$.next({
            provider,
            projectId: projectIdForQueue,
            queueStatusId: queueStatus.id,
          });

          const progressState = this.getProgressState$(
            provider,
            projectIdForQueue,
            true,
          ) as BehaviorSubject<ProgressBarState>;

          progressState.next({
            isVisible: true,
            queueStatusId: queueStatus.id,
            percentage: queueStatus.latest_progress.percentage,
            currentCount: queueStatus.latest_progress.current_count,
            totalCount: queueStatus.latest_progress.total_count,
            message: queueStatus.latest_progress.message,
            arabicMessage: queueStatus.latest_progress.arabic_message,
            // The run's status decides the outcome; latest_progress.status is the last job's own
            // status ('succeed' / 'failed') and would leave a finished run looking in progress
            status: this.mapQueueStatusToProgressStatus(queueStatus.status),
          });
        }
      },
      error: (error) => {
        console.error(`Error checking last completed queue for ${provider}:`, error);
      },
    });
  }

  /**
   * Transform raw results to typed results based on provider
   */
  private transformToTypedResult(
    provider: SyncProvider,
    rawResults: QueueTrackerData,
  ): TypedSyncResult {
    switch (provider) {
      case 'projects': {
        const projectsResults = rawResults.results as ProjectsSyncResult;
        const successRate = Math.round(
          ((projectsResults.projects_created +
            projectsResults.projects_updated +
            projectsResults.projects_matched) /
            (projectsResults.projects_created +
              projectsResults.projects_updated +
              projectsResults.projects_matched +
              projectsResults.projects_failed)) *
            100,
        );

        return {
          type: 'projects',
          status: rawResults.status,
          timestamp: rawResults.timestamp,
          provider: rawResults.provider,
          failed: rawResults.failed || 0,
          projects_created: projectsResults.projects_created || 0,
          projects_updated: projectsResults.projects_updated || 0,
          projects_matched: projectsResults.projects_matched || 0,
          projects_failed: projectsResults.projects_failed || 0,
          //todo: add summary
          summary: {
            success_rate: successRate,
          },
          meta_data: projectsResults.meta_data || {
            projects_created: [],
            projects_updated: [],
            projects_matched: [],
            projects_failed: [],
          },
          failures: projectsResults.failures || [],
        } as ProjectsSyncResult;
      }

      case 'p6': {
        const p6Results = rawResults.results as P6SyncResult;
        const successRate = Math.round(
          ((p6Results.created_count + p6Results.updated_count + p6Results.matched_count) /
            (p6Results.created_count +
              p6Results.updated_count +
              p6Results.matched_count +
              p6Results.failed_count)) *
            100,
        );

        return {
          type: 'p6',
          status: rawResults.status,
          timestamp: new Date(rawResults.timestamp),
          provider: rawResults.provider,
          failed: rawResults.failed || 0,
          summary: {
            success_rate: successRate,
          },
          created_count: p6Results.created_count || 0,
          updated_count: p6Results.updated_count || 0,
          matched_count: p6Results.matched_count || 0,
          failed_count: p6Results.failed_count || 0,
          deleted_count: p6Results.deleted_count || 0,
          failed_p6_reports: p6Results.failed_p6_reports || [],
        } as P6SyncResult;
      }

      case 'observations': {
        // A failed run may carry no aggregated results (the backend sends an empty array)
        const observationsResults = ((rawResults.results as ObservationsSyncResult)
          ?.aggregated_results || {}) as Partial<ObservationsSyncResult['aggregated_results']>;

        const createdCount = observationsResults.created_count || 0;
        const updatedCount = observationsResults.updated_count || 0;
        const matchedCount = observationsResults.matched_count || 0;
        const failedCount = observationsResults.failed_count || 0;
        const totalCount = createdCount + updatedCount + matchedCount + failedCount;
        const successRate = totalCount
          ? Math.floor(((createdCount + updatedCount + matchedCount) / totalCount) * 100)
          : 0;
        return {
          type: 'observations',
          status: rawResults.status,
          timestamp: new Date(rawResults.timestamp),
          provider: rawResults.provider,
          failed: rawResults.failed || 0,
          summary: {
            success_rate: successRate,
          },
          created_count: createdCount,
          updated_count: updatedCount,
          matched_count: matchedCount,
          failed_count: failedCount,
          failed_observations: ((observationsResults.failed_observations || []) as any[]).flat(),
        } as ObservationsSyncResult;
      }
      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Load queue tracker from localStorage
   */
  private loadQueueTracker(provider: SyncProvider, projectId?: string) {
    const key = this.getProjectSpecificKey(provider, projectId);
    return (
      JSON.parse(localStorage.getItem(key) || '{}') || {
        id: null,
        completed: false,
        viewed: false,
        results: undefined,
      }
    );
  }

  /**
   * Save queue tracker to localStorage
   */
  private saveQueueTracker(provider: SyncProvider, projectId?: string, tracker?: QueueTrackerData) {
    const key = this.getProjectSpecificKey(provider, projectId);
    const dataToSave = tracker || this.queueTrackers[provider];
    dataToSave.provider = provider;
    localStorage.setItem(key, JSON.stringify(dataToSave));
  }

  /**
   * Handle queue status updates from polling
   */
  private handleQueueStatusUpdate(
    queueStatuses: QueueStatus[],
    provider: SyncProvider = 'projects',
    projectId?: string,
  ): void {
    // Filter for the specific provider and, when applicable, the current project
    const expectedProjectId =
      provider === 'p6' || provider === 'observations' ? projectId?.toString() : undefined;
    const providerJobs = queueStatuses.filter((status) => {
      if (status.provider !== provider) {
        return false;
      }

      if (expectedProjectId && status.project_id?.toString() !== expectedProjectId) {
        // Active queue belongs to a different project; ignore it to avoid cross-project polling
        console.warn(
          `Ignoring active queue ${status.id} for provider ${provider} with project ${status.project_id} when expecting ${expectedProjectId}`,
        );
        return false;
      }

      return true;
    });

    if (providerJobs.length > 0) {
      const lastActiveJob = providerJobs[0];

      // Use passed projectId if available, otherwise try to get it from the job
      const projectIdForJob = projectId || lastActiveJob.project_id;
      const queueTracker = this.getQueueTracker(provider, projectIdForJob?.toString());
      if (lastActiveJob.id === queueTracker.id) {
        if (!queueTracker.completed) {
          if (!this._currentQueueStatusId) {
            //if no current job, start tracking
            this.startTracking(lastActiveJob.id, projectIdForJob?.toString());
          }
        }
      }

      if (lastActiveJob.id !== queueTracker.id) {
        this.updateQueueTracker(
          provider,
          {
            id: lastActiveJob.id,
            completed: false,
            viewed: false,
          },
          projectIdForJob?.toString(),
        );

        // Save the queue status ID to localStorage for persistence
        this.saveLastQueueStatusId(provider, lastActiveJob.id, projectIdForJob?.toString());

        this.startTracking(lastActiveJob.id, projectIdForJob?.toString());
      }
    } else {
      // No active jobs, stop tracking
      this.stopTracking();
    }
  }

  /**
   * Update progress from queue status ID
   */
  private updateProgressFromQueueStatusId(queueStatusId: number, projectId?: string): void {
    this.getQueueStatusDetails(queueStatusId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (queueStatus: QueueStatus) => {
          this.updateProgress(queueStatus, projectId);
        },
        error: (error) => {
          console.error('Error updating progress:', error);
        },
      });
  }

  /**
   * Update progress from queue status data
   */
  private updateProgress(queueStatus: QueueStatus, projectId?: string): void {
    const latestProgress = queueStatus.latest_progress;

    if (!latestProgress) {
      // console.warn('No progress found for queueStatus:', queueStatus);
      return;
    }

    console.debug(
      `latestProgress: provider: ${queueStatus.provider} and projectId: ${queueStatus.project_id}`,
      latestProgress,
    );

    console.debug('Full queueStatus object:', queueStatus);

    const progressState: ProgressBarState = {
      isVisible: true,
      percentage: latestProgress.percentage,
      currentCount: latestProgress.current_count,
      totalCount: latestProgress.total_count,
      message: latestProgress.message,
      arabicMessage: latestProgress.arabic_message,
      status: this.mapQueueStatusToProgressStatus(queueStatus.status),
      queueStatusId: queueStatus.id,
    };

    // Update progress state based on provider and project
    if (queueStatus.provider === 'projects') {
      // Projects sync is global
      this._progressStates$[queueStatus.provider].next(progressState);
    } else if (queueStatus.provider === 'p6' || queueStatus.provider === 'observations') {
      // P6 and observations are project-specific
      const effectiveProjectId = queueStatus.project_id || projectId;

      if (effectiveProjectId) {
        const key = `${queueStatus.provider}_${effectiveProjectId}`;

        // Create project-specific progress state if it doesn't exist
        if (!this._projectProgressStates$.has(key)) {
          this._projectProgressStates$.set(
            key,
            new BehaviorSubject<ProgressBarState>({
              isVisible: false,
              percentage: 0,
              currentCount: 0,
              totalCount: 0,
              message: '',
              arabicMessage: '',
              status: 'processing',
            }),
          );
        }

        // Update project-specific progress state
        this._projectProgressStates$.get(key)!.next(progressState);
      } else {
        console.warn(`Project ID missing for ${queueStatus.provider} progress update`);
        // Fallback to provider-specific state
        this._progressStates$[queueStatus.provider].next(progressState);
      }
    }

    // Handle completion
    if (
      queueStatus.completed_at ||
      latestProgress.percentage === 100 ||
      queueStatus.status === 'completed' ||
      queueStatus.status === 'failed'
    ) {
      this.handleCompletion(queueStatus);
    }
  }

  /**
   * Map queue status to progress status
   */
  private mapQueueStatusToProgressStatus(
    queueStatus: string,
  ): 'processing' | 'completed' | 'failed' {
    switch (queueStatus) {
      case 'success':
        return 'completed';
      case 'completed':
        return 'completed';
      case 'failed':
        return 'failed';
      default:
        return 'processing';
    }
  }

  /**
   * Handle completion of sync operation
   */
  private handleCompletion(queueStatus: QueueStatus): void {
    // Find the provider for this queue status
    const provider = queueStatus.provider as SyncProvider;

    // Stop tracking this specific job
    const projectIdForJob = queueStatus.project_id;
    const trackerForCompletion = this.getQueueTracker(provider, projectIdForJob?.toString());

    if (trackerForCompletion?.id === queueStatus.id) {
      this.stopTracking(this.SYNC_RESULTS_MODAL_TIMEOUT);
    }

    this.updateQueueTracker(
      provider,
      {
        id: queueStatus.id,
        completed: true,
        status: queueStatus.status,
        viewed: false,
        results: queueStatus.latest_progress.results,
        timestamp: queueStatus.completed_at ? parseServerDate(queueStatus.completed_at) : undefined,
        provider: provider,
      },
      projectIdForJob?.toString(),
    );

    // Remove the saved queue status ID since the job is now completed
    this.removeLastQueueStatusId(provider, projectIdForJob?.toString());

    // Notify listeners that a queue for this provider (and project, if applicable) has completed
    this._queueCompleted$.next({
      provider,
      projectId: projectIdForJob?.toString(),
      queueStatusId: queueStatus.id,
    });
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): Observable<never> {
    console.error('Queue Status Service Error:', error);
    return throwError(() => error);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopTracking();

    // Clear any remaining timeouts as a safety measure
    if (this._pollingTimeoutId !== null) {
      clearTimeout(this._pollingTimeoutId);
      this._pollingTimeoutId = null;
    }
    if (this._trackingTimeoutId !== null) {
      clearTimeout(this._trackingTimeoutId);
      this._trackingTimeoutId = null;
    }
    if (this._mainTrackingTimeoutId !== null) {
      clearTimeout(this._mainTrackingTimeoutId);
      this._mainTrackingTimeoutId = null;
    }

    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
